import React, { useState, useEffect, useCallback } from 'react';
import { operacionesApi, ventasApi, ordenesApi } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/NumberInput';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { OTDatosModal } from '../components/activo-circulante/OTDatosModal';
import { EnvioGerenciaDialog } from '../components/activo-circulante/EnvioGerenciaDialog';
import { FacturasSinOTModal } from '../components/activo-circulante/FacturasSinOTModal';
import { Printer, TrendingUp, RefreshCw, ChevronDown, ChevronRight, FileText, X, FolderOpen, Folder, MoreVertical, AlertTriangle, FileSpreadsheet, FileDown, Mail, Search, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { printHTML, buildReport, fmtCLP, exportToCSV } from '../lib/printUtils';
import { fmtFecha } from '../lib/utils'; // Iter 292.h — DD-MM-YYYY canónico
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

export const ActivoCirculantePage = () => {
  const [loading, setLoading] = useState(true);
  const [otModal, setOtModal] = useState(null);
  const [envioOpen, setEnvioOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedProyectos, setExpandedProyectos] = useState({});
  const [expandedSubproyectos, setExpandedSubproyectos] = useState({});
  const [showFacturas, setShowFacturas] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [_savingOT, setSavingOT] = useState(null);
  // Iter 294.b: facturas de venta sin OT asignada
  const [sinOTInfo, setSinOTInfo] = useState(null);
  const [showSinOT, setShowSinOT] = useState(false);
  // Iter 313.j.18 (Renzo, feb-2026) — buscador global (Cliente / Proyecto / OT / Proforma / Factura)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchItems, setSearchItems] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Iter 313.j.22 (Renzo, feb-2026) — anulación manual de Proformas desde el modal
  const [anularModal, setAnularModal] = useState(null); // {venta} | null
  const [anularMotivo, setAnularMotivo] = useState('');
  const [anularDocFacturado, setAnularDocFacturado] = useState('');
  const [anularSaving, setAnularSaving] = useState(false);

  useEffect(() => { loadData(); loadSinOT(); }, []);

  // Iter 313.j.18 — búsqueda debounced 250ms
  useEffect(() => {
    const q = (searchQuery || '').trim();
    if (q.length < 2) { setSearchItems([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await api.get('/ventas/activo-circulante/search', { params: { q, limit: 30 } });
        setSearchItems(r.data?.results || []);
      } catch { setSearchItems([]); }
      finally { setSearchLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleSearchSelect = (sel) => {
    if (!sel) return;
    const otNum = sel.ot_numero;
    if (otNum) {
      // Buscar la descripción de la OT en el árbol para el título del modal
      let desc = '';
      (data?.proyectos || []).forEach(p => (p.subproyectos || []).forEach(sp => (sp.ots || []).forEach(o => { if (o.ot_numero === otNum) desc = o.descripcion || ''; })));
      // Iter 313.j.24 (Renzo, feb-2026): fallback a la descripción devuelta por el
      // backend cuando la OT no está en el árbol local (ej: búsqueda por número
      // de OT antigua o de otro año). Así el modal siempre muestra el nombre.
      if (!desc && sel.descripcion) desc = sel.descripcion;
      // Expandir proyecto/subproyecto que contiene la OT (para que quede visible al cerrar modal)
      (data?.proyectos || []).forEach(p => (p.subproyectos || []).forEach(sp => (sp.ots || []).forEach(o => {
        if (o.ot_numero === otNum) {
          setExpandedProyectos(prev => ({ ...prev, [p.proyecto_codigo]: true }));
          setExpandedSubproyectos(prev => ({ ...prev, [`${p.proyecto_codigo}-${sp.orden_compra}`]: true }));
        }
      })));
      handleVerFacturasOT(otNum, desc);
      // Scroll a la fila
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="ot-row-${otNum}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
    setSearchQuery('');
    setSearchItems([]);
  };

  const loadSinOT = async () => {
    try {
      const res = await ventasApi.getSinOT({ anio: 0 });
      setSinOTInfo(res.data);
    } catch { /* banner opcional, no bloquear */ }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await operacionesApi.getActivoCirculante();
      setData(res.data);
      const allProyectos = {};
      const allSubproyectos = {};
      res.data.proyectos?.forEach(p => { 
        allProyectos[p.proyecto_codigo] = true;
        p.subproyectos?.forEach(sp => {
          allSubproyectos[`${p.proyecto_codigo}-${sp.orden_compra}`] = true;
        });
      });
      setExpandedProyectos(allProyectos);
      setExpandedSubproyectos(allSubproyectos);
    } catch (err) {
      setError('Error al cargar los datos');

    } finally {
      setLoading(false);
    }
  };

  const handleVerFacturasOT = async (otNumero, descripcion) => {
    setLoadingFacturas(true);
    setShowFacturas({ tipo: 'ot', id: otNumero, nombre: `OT ${otNumero} - ${descripcion}` });
    try {
      const res = await ventasApi.getPorOT(otNumero);
      setFacturas(res.data.ventas || []);
    } catch (_err) {
      setFacturas([]);
    } finally {
      setLoadingFacturas(false);
    }
  };

  const closeFacturas = () => { setShowFacturas(null); setFacturas([]); };

  // Iter 313.j.22 — abrir modal de anulación manual para una Proforma
  const openAnularProforma = (venta) => {
    setAnularModal({ venta });
    setAnularMotivo('');
    setAnularDocFacturado('');
  };
  const closeAnularProforma = () => {
    setAnularModal(null);
    setAnularMotivo('');
    setAnularDocFacturado('');
    setAnularSaving(false);
  };
  const confirmarAnularProforma = async () => {
    if (!anularModal?.venta) return;
    const motivo = (anularMotivo || '').trim();
    const doc = (anularDocFacturado || '').trim();
    if (!motivo) { toast.error('Ingresa el motivo de anulación'); return; }
    if (!doc) { toast.error('Indica el documento donde quedó facturado'); return; }
    setAnularSaving(true);
    try {
      await api.post(`/ventas/${anularModal.venta.id_venta}/anular-proforma`, { motivo, doc_facturado: doc });
      toast.success(`Proforma ${anularModal.venta.numero_documento_display || anularModal.venta.numero_factura} anulada`);
      // Refrescar el modal actual con la nueva data
      if (showFacturas?.tipo === 'ot') {
        const res = await ventasApi.getPorOT(showFacturas.id);
        setFacturas(res.data.ventas || []);
      }
      loadData(); // refresca totales del árbol
      closeAnularProforma();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error al anular';
      toast.error(detail);
    } finally {
      setAnularSaving(false);
    }
  };

  const startEditing = (otNumero, field, currentValue) => {
    setEditingCell({ otNumero, field });
    setEditValue(currentValue?.toString() || '0');
  };

  const cancelEditing = () => { setEditingCell(null); setEditValue(''); };

  const saveEdit = useCallback(async () => {
    if (!editingCell) return;
    const { otNumero, field } = editingCell;
    const numValue = parseFloat(editValue) || 0;
    setSavingOT(otNumero);
    try {
      const payload = {};
      payload[field] = numValue;
      await operacionesApi.updateOTCirculante(otNumero, payload);
      toast.success(`OT ${otNumero} actualizada`);
      loadData();
    } catch (_err) {
      toast.error('Error al guardar');
    } finally {
      setSavingOT(null);
      setEditingCell(null);
      setEditValue('');
    }
  }, [editingCell, editValue]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    else if (e.key === 'Escape') cancelEditing();
  };

  const irADatosOT = (ot) => {
    if (!ot.id) { toast.error('OT sin identificador — recarga la página'); return; }
    setOtModal(ot);
  };

  const cambiarEstadoOT = async (ot, action) => {
    if (!ot.id) { toast.error('OT sin identificador — recarga la página'); return; }
    try {
      if (action === 'terminar') {
        await ordenesApi.patch(ot.id, { estado: 'TERMINADA' });
        toast.success(`OT ${ot.numero_ot} marcada TERMINADA`);
      } else if (action === 'ejecutar') {
        await ordenesApi.patch(ot.id, { estado: 'EN EJECUCION' });
        toast.success(`OT ${ot.numero_ot} marcada EN EJECUCIÓN`);
      }
      loadData();
    } catch {
      toast.error('Error al actualizar la OT');
    }
  };

  const esSobrefacturada = (ot) => (ot.monto || 0) > 0 && (ot.facturado || 0) > (ot.monto || 0);

  const contarSobrefacturadas = () => {
    let n = 0;
    data?.proyectos?.forEach(p => {
      const all = [...(p.ots_individuales || []), ...(p.ots_sin_oc || [])];
      p.subproyectos?.forEach(sp => { all.push(...(sp.ots || []), ...(sp.aumentos_obra || [])); });
      all.forEach(ot => { if (esSobrefacturada(ot)) n++; });
    });
    return n;
  };

  const handleExcel = () => {
    if (!data?.proyectos?.length) { toast.warning('Nada para exportar'); return; }
    const rows = [];
    data.proyectos.forEach(p => {
      const push = (ot, grupo) => rows.push({
        proyecto: p.proyecto_codigo, cliente: p.cliente, grupo,
        numero_ot: ot.numero_ot, tipo: ot.es_aumento_obra ? 'AO' : 'OT',
        descripcion: ot.descripcion, estado: ot.estado || '',
        monto: ot.monto, facturado: ot.facturado, saldo: ot.saldo_retencion,
        av_fin: ot.av_financiero, x_facturar: ot.por_facturar_ganado,
        av_fis: ot.av_fisico, prox_mes: ot.fact_prox_mes,
        sobrefacturada: esSobrefacturada(ot) ? 'SÍ' : '',
      });
      p.subproyectos?.forEach(sp => {
        sp.ots?.forEach(ot => push(ot, `O/C ${sp.orden_compra}`));
        sp.aumentos_obra?.forEach(ao => push(ao, `O/C ${sp.orden_compra}`));
      });
      p.ots_individuales?.forEach(ot => push(ot, 'Individual'));
      p.ots_sin_oc?.forEach(ot => push(ot, 'Sin O/C'));
    });
    exportToCSV({
      filename: `activo_circulante_${new Date().toISOString().slice(0, 10)}`,
      title: 'CHIRINO STEEL · Activo Circulante (valores netos)',
      summary: [
        { label: 'Monto total', value: data.totales.monto },
        { label: 'Facturado', value: data.totales.facturado },
        { label: 'Saldo/Retención', value: data.totales.saldo_retencion },
        { label: 'x Facturar ganado', value: data.totales.por_facturar_ganado },
        { label: 'Fact. próx. mes', value: data.totales.fact_prox_mes },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'proyecto', label: 'Proyecto' }, { key: 'cliente', label: 'Cliente' },
        { key: 'grupo', label: 'Grupo' }, { key: 'numero_ot', label: 'OT' },
        { key: 'tipo', label: 'Tipo' }, { key: 'descripcion', label: 'Descripción' },
        { key: 'estado', label: 'Estado' }, { key: 'monto', label: 'Monto' },
        { key: 'facturado', label: 'Facturado' }, { key: 'saldo', label: 'Saldo/Retención' },
        { key: 'av_fin', label: 'AV.Fin %' }, { key: 'x_facturar', label: 'x Facturar' },
        { key: 'av_fis', label: 'AV.Fis %' }, { key: 'prox_mes', label: 'Fact.Próx.Mes' },
        { key: 'sobrefacturada', label: 'Sobre-facturada' },
      ],
      rows,
    });
  };

  const handlePDF = async () => {
    try {
      toast.info('Generando PDF...');
      const res = await operacionesApi.getActivoCirculantePDF();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Activo_Circulante_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al generar PDF');
    }
  };

  const handlePrint = () => {
    if (!data) return;
    
    let tableRows = '';
    data.proyectos.forEach(proy => {
      tableRows += `<tr style="background:#1a365d;color:white;font-weight:bold;">
        <td colspan="9" style="border:1px solid #333;padding:6px;">📁 ${proy.proyecto_codigo} - ${proy.cliente} | Total: $${fmt(proy.totales.monto)}</td>
      </tr>`;
      
      // Subproyectos (grupos con más de 1 OT)
      proy.subproyectos?.forEach(sp => {
        tableRows += `<tr style="background:#bee3f8;font-weight:bold;">
          <td colspan="2" style="border:1px solid #666;padding:4px;">📂 ${sp.nombre?.substring(0,50)}...</td>
          <td style="border:1px solid #666;padding:4px;text-align:right;">$${fmt(sp.total_combinado?.monto)}</td>
          <td style="border:1px solid #666;padding:4px;text-align:right;">$${fmt(sp.total_combinado?.facturado)}</td>
          <td style="border:1px solid #666;padding:4px;text-align:right;">$${fmt(sp.total_combinado?.saldo_retencion)}</td>
          <td style="border:1px solid #666;padding:4px;"></td>
          <td style="border:1px solid #666;padding:4px;text-align:right;">$${fmt(sp.total_combinado?.por_facturar_ganado)}</td>
          <td style="border:1px solid #666;padding:4px;"></td>
          <td style="border:1px solid #666;padding:4px;text-align:right;">$${fmt(sp.total_combinado?.fact_prox_mes)}</td>
        </tr>`;
        sp.ots?.forEach((ot, idx) => {
          const bg = idx % 2 === 0 ? '#fff' : '#f7fafc';
          tableRows += `<tr style="background:${bg};"><td style="border:1px solid #999;padding:3px;text-align:center;">${ot.numero_ot}</td>
            <td style="border:1px solid #999;padding:3px;padding-left:30px;">${ot.descripcion}</td>
            <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.monto)}</td>
            <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.facturado)}</td>
            <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.saldo_retencion)}</td>
            <td style="border:1px solid #999;padding:3px;text-align:center;">${ot.av_financiero}%</td>
            <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.por_facturar_ganado)}</td>
            <td style="border:1px solid #999;padding:3px;text-align:center;">${ot.av_fisico}%</td>
            <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.fact_prox_mes)}</td>
          </tr>`;
        });
        if (sp.aumentos_obra?.length > 0) {
          tableRows += `<tr style="background:#fef3c7;"><td colspan="9" style="border:1px solid #666;padding:3px;font-weight:bold;color:#92400e;">🔸 AUMENTOS DE OBRA</td></tr>`;
          sp.aumentos_obra.forEach((ao, idx) => {
            const bg = idx % 2 === 0 ? '#fffbeb' : '#fef9c3';
            tableRows += `<tr style="background:${bg};"><td style="border:1px solid #999;padding:3px;text-align:center;">${ao.numero_ot}</td>
              <td style="border:1px solid #999;padding:3px;padding-left:40px;font-style:italic;">(AO) ${ao.descripcion}</td>
              <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ao.monto)}</td>
              <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ao.facturado)}</td>
              <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ao.saldo_retencion)}</td>
              <td style="border:1px solid #999;padding:3px;text-align:center;">${ao.av_financiero}%</td>
              <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ao.por_facturar_ganado)}</td>
              <td style="border:1px solid #999;padding:3px;text-align:center;">${ao.av_fisico}%</td>
              <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ao.fact_prox_mes)}</td>
            </tr>`;
          });
        }
      });
      
      // OTs individuales (con O/C pero solo 1 OT)
      proy.ots_individuales?.forEach((ot, idx) => {
        const bg = idx % 2 === 0 ? '#fff' : '#f7fafc';
        tableRows += `<tr style="background:${bg};"><td style="border:1px solid #999;padding:3px;text-align:center;">${ot.numero_ot}</td>
          <td style="border:1px solid #999;padding:3px;">${ot.descripcion}</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.monto)}</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.facturado)}</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.saldo_retencion)}</td>
          <td style="border:1px solid #999;padding:3px;text-align:center;">${ot.av_financiero}%</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.por_facturar_ganado)}</td>
          <td style="border:1px solid #999;padding:3px;text-align:center;">${ot.av_fisico}%</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;">$${fmt(ot.fact_prox_mes)}</td>
        </tr>`;
      });
      
      // OTs sin O/C (al final)
      proy.ots_sin_oc?.forEach((ot, idx) => {
        const bg = idx % 2 === 0 ? '#fafafa' : '#f0f0f0';
        tableRows += `<tr style="background:${bg};"><td style="border:1px solid #999;padding:3px;text-align:center;color:#666;">${ot.numero_ot}</td>
          <td style="border:1px solid #999;padding:3px;color:#666;">${ot.descripcion}</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;color:#666;">$${fmt(ot.monto)}</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;color:#666;">$${fmt(ot.facturado)}</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;color:#666;">$${fmt(ot.saldo_retencion)}</td>
          <td style="border:1px solid #999;padding:3px;text-align:center;color:#666;">${ot.av_financiero}%</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;color:#666;">$${fmt(ot.por_facturar_ganado)}</td>
          <td style="border:1px solid #999;padding:3px;text-align:center;color:#666;">${ot.av_fisico}%</td>
          <td style="border:1px solid #999;padding:3px;text-align:right;color:#666;">$${fmt(ot.fact_prox_mes)}</td>
        </tr>`;
      });
    });

    const printContent = buildReport({
      title: 'INFORME ACTIVO CIRCULANTE',
      subtitle: `Proyectos: ${data.totales.cantidad_proyectos} | OTs: ${data.totales.cantidad_ots} | ${new Date().toLocaleDateString('es-CL')}`,
      summaryBoxes: [
        { label: 'Monto Total', value: `$${fmtCLP(data.totales.monto)}` },
        { label: 'Facturado', value: `$${fmtCLP(data.totales.facturado)}`, color: '#15803d' },
        { label: 'Saldo', value: `$${fmtCLP(data.totales.saldo_retencion)}`, color: '#1d4ed8' },
        { label: 'x Facturar', value: `$${fmtCLP(data.totales.por_facturar_ganado)}`, color: '#c2410c' },
        { label: 'Fact. Prox Mes', value: `$${fmtCLP(data.totales.fact_prox_mes)}`, color: '#553c9a', highlight: true },
      ],
      tableHTML: `<table><thead><tr><th style="width:35px;">OT</th><th>Descripcion</th><th style="width:60px;">Monto</th><th style="width:60px;">Fact.</th><th style="width:60px;">Saldo</th><th style="width:35px;">AV.Fin</th><th style="width:60px;">x Fact</th><th style="width:35px;">AV.Fis</th><th style="width:60px;">Prox.Mes</th></tr></thead>
      <tbody>${tableRows}
      <tr class="totals-row"><td colspan="2" style="border:1px solid #1a365d;padding:5px;">TOTALES GENERALES</td>
      <td style="border:1px solid #1a365d;padding:5px;text-align:right;">$${fmt(data.totales.monto)}</td>
      <td style="border:1px solid #1a365d;padding:5px;text-align:right;">$${fmt(data.totales.facturado)}</td>
      <td style="border:1px solid #1a365d;padding:5px;text-align:right;">$${fmt(data.totales.saldo_retencion)}</td>
      <td style="border:1px solid #1a365d;padding:5px;"></td>
      <td style="border:1px solid #1a365d;padding:5px;text-align:right;">$${fmt(data.totales.por_facturar_ganado)}</td>
      <td style="border:1px solid #1a365d;padding:5px;"></td>
      <td style="border:1px solid #1a365d;padding:5px;text-align:right;">$${fmt(data.totales.fact_prox_mes)}</td></tr></tbody></table>`,
    });

    printHTML(printContent, { title: 'Activo Circulante - CHIRINO STEEL', isFullHTML: true });
  };

  const fmt = (num) => num?.toLocaleString('es-CL') || '0';
  const toggleProyecto = (codigo) => setExpandedProyectos(prev => ({ ...prev, [codigo]: !prev[codigo] }));
  const toggleSubproyecto = (proyectoCodigo, ordenCompra) => {
    const key = `${proyectoCodigo}-${ordenCompra}`;
    setExpandedSubproyectos(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const expandAll = () => {
    const allP = {}, allS = {};
    data?.proyectos?.forEach(p => { 
      allP[p.proyecto_codigo] = true;
      p.subproyectos?.forEach(sp => { allS[`${p.proyecto_codigo}-${sp.orden_compra}`] = true; });
    });
    setExpandedProyectos(allP);
    setExpandedSubproyectos(allS);
  };
  const collapseAll = () => { setExpandedProyectos({}); setExpandedSubproyectos({}); };

  const renderEditableCell = (ot, field, value, className = '') => {
    const isEditing = editingCell?.otNumero === ot.numero_ot && editingCell?.field === field;
    if (isEditing) {
      return (
        <NumberInput allowNegative={false} allowDecimal={editingCell?.field !== 'fact_prox_mes'} value={editValue} onChange={(clean) => setEditValue(clean)}
          onKeyDown={handleKeyDown} onBlur={saveEdit} autoFocus className="h-5 w-16 text-[10px] p-1 text-center" />
      );
    }
    const displayValue = field === 'fact_prox_mes' ? `$${fmt(value)}` : `${value}%`;
    return (
      <span className={`cursor-pointer hover:bg-yellow-100 px-1 rounded ${className}`}
        onClick={() => startEditing(ot.numero_ot, field, value)} title="Click para editar">{displayValue}</span>
    );
  };

  const renderAcciones = (ot) => (
    <div className="flex items-center">
      <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => handleVerFacturasOT(ot.numero_ot, ot.descripcion)} title="Ver facturas">
        <FileText className="w-3 h-3 text-gray-500" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" data-testid={`menu-ot-${ot.numero_ot}`} title="Cambiar estado de la OT">
            <MoreVertical className="w-3 h-3 text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {ot.estado === 'EN EJECUCION' ? (
            <DropdownMenuItem className="text-xs" onClick={() => cambiarEstadoOT(ot, 'terminar')} data-testid={`accion-terminar-${ot.numero_ot}`}>
              Marcar TERMINADA
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem className="text-xs" onClick={() => cambiarEstadoOT(ot, 'ejecutar')} data-testid={`accion-ejecutar-${ot.numero_ot}`}>
              Marcar EN EJECUCIÓN
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className="text-xs text-blue-700 focus:text-blue-700" onClick={() => irADatosOT(ot)} data-testid={`accion-datos-${ot.numero_ot}`}>
            Datos de la OT (editar / cerrar financieramente)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const renderSaldoCell = (ot, borderClass = 'border-gray-200') => (
    <td className={`border-b ${borderClass} p-1 text-right font-mono ${esSobrefacturada(ot) ? 'text-red-600 font-bold bg-red-50' : 'text-blue-600'}`}
      title={esSobrefacturada(ot) ? `⚠ Sobre-facturada: facturado $${fmt(ot.facturado)} > monto $${fmt(ot.monto)}` : ''}
      data-testid={esSobrefacturada(ot) ? `saldo-sobrefact-${ot.numero_ot}` : undefined}>
      {esSobrefacturada(ot) && <AlertTriangle className="w-3 h-3 inline mr-0.5 text-red-600" />}${fmt(ot.saldo_retencion)}
    </td>
  );

  const renderOTRow = (ot, indent = 0, bgClass = '') => (
    <tr key={ot.numero_ot} className={bgClass || 'bg-white hover:bg-gray-50'}>
      <td className="border-b border-gray-200 p-1 text-center font-mono whitespace-nowrap" style={{paddingLeft: `${indent}px`}}>
        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${ot.estado === 'EN EJECUCION' ? 'bg-green-500' : 'bg-gray-400'}`} title={ot.estado || 'Sin estado'}></span>
        <button onClick={() => irADatosOT(ot)} className="text-blue-700 font-semibold hover:underline cursor-pointer" title="Abrir Datos de la OT" data-testid={`link-ot-${ot.numero_ot}`}>{ot.numero_ot}</button>
      </td>
      <td className="border-b border-gray-200 p-1 truncate max-w-[200px]" style={{paddingLeft: `${indent}px`}}>{ot.descripcion}</td>
      <td className="border-b border-gray-200 p-1 text-right font-mono">${fmt(ot.monto)}</td>
      <td className="border-b border-gray-200 p-1 text-right font-mono text-green-700">${fmt(ot.facturado)}</td>
      {renderSaldoCell(ot)}
      <td className="border-b border-gray-200 p-1 text-center bg-yellow-50">{renderEditableCell(ot, 'av_financiero', ot.av_financiero)}</td>
      <td className="border-b border-gray-200 p-1 text-right font-mono text-orange-600">${fmt(ot.por_facturar_ganado)}</td>
      <td className="border-b border-gray-200 p-1 text-center bg-yellow-50">{renderEditableCell(ot, 'av_fisico', ot.av_fisico)}</td>
      <td className="border-b border-gray-200 p-1 text-center bg-purple-50">{renderEditableCell(ot, 'fact_prox_mes', ot.fact_prox_mes, 'text-purple-700')}</td>
      <td className="border-b border-gray-200 p-1">{renderAcciones(ot)}</td>
    </tr>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  if (error || !data?.proyectos?.length) return <div className="p-4 text-center"><p className="text-destructive mb-4">{error || 'No hay datos'}</p><Button onClick={loadData}>Reintentar</Button></div>;

  return (
    <div className="p-2" data-testid="activo-circulante-page">
      <div className="flex justify-between items-center mb-2">
        <div>
          <h1 className="text-lg font-bold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />Activo Circulante</h1>
          <p className="text-xs text-muted-foreground">Ordenado: Cliente → Proyecto → Subproyectos → OTs individuales → Sin O/C</p>
        </div>
        <div className="flex gap-1">
          <Button onClick={expandAll} variant="outline" size="sm" className="h-7 text-xs">Expandir</Button>
          <Button onClick={collapseAll} variant="outline" size="sm" className="h-7 text-xs">Colapsar</Button>
          <Button onClick={loadData} variant="outline" size="sm" className="h-7 w-7 p-0"><RefreshCw className="w-3 h-3" /></Button>
          <Button onClick={handleExcel} variant="outline" size="sm" className="h-7 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-excel-circulante"><FileSpreadsheet className="w-3 h-3 mr-1" />Excel</Button>
          <Button onClick={handlePDF} variant="outline" size="sm" className="h-7 px-2 text-xs text-red-700 border-red-300 hover:bg-red-50" data-testid="btn-pdf-circulante"><FileDown className="w-3 h-3 mr-1" />PDF</Button>
          <Button onClick={() => setEnvioOpen(true)} variant="outline" size="sm" className="h-7 px-2 text-xs text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="btn-envio-gerencia"><Mail className="w-3 h-3 mr-1" />Envío Gerencia</Button>
          <Button onClick={handlePrint} variant="outline" size="sm" className="h-7 px-2 text-xs"><Printer className="w-3 h-3 mr-1" />Imprimir</Button>
        </div>
      </div>

      {/* Iter 313.j.18 (Renzo, feb-2026) — Buscador global */}
      <div className="mb-3 relative" data-testid="buscador-global-wrapper">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 shrink-0">
            <Search className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-semibold text-gray-700">Buscar:</span>
          </div>
          <div className="relative flex-1 max-w-2xl">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={(e) => { if (searchItems.length) e.target.select(); }}
              placeholder="Cliente, proyecto, OT, N° proforma o N° factura… (≥ 2 caracteres)"
              className="h-8 text-xs pr-8"
              data-testid="buscador-global-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setSearchItems([]); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                data-testid="buscador-global-clear"
              ><X className="w-3.5 h-3.5" /></button>
            )}
            {/* Dropdown de resultados */}
            {searchQuery.length >= 2 && (searchItems.length > 0 || searchLoading) && (
              <div
                className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[60vh] overflow-y-auto"
                data-testid="buscador-global-dropdown"
              >
                {searchLoading ? (
                  <div className="px-3 py-3 text-xs text-gray-500 flex items-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Buscando…
                  </div>
                ) : searchItems.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-gray-400 italic">Sin coincidencias</div>
                ) : searchItems.map((it) => {
                  const badge = it.type === 'ot' ? { txt: 'OT', cls: 'bg-blue-100 text-blue-700' } :
                                it.type === 'factura' ? { txt: 'Factura', cls: 'bg-emerald-100 text-emerald-700' } :
                                it.type === 'proforma' ? { txt: 'Proforma', cls: 'bg-amber-100 text-amber-800' } :
                                { txt: 'Cliente', cls: 'bg-purple-100 text-purple-700' };
                  return (
                    <button
                      key={it.value}
                      type="button"
                      onClick={() => handleSearchSelect(it)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 flex items-center gap-2"
                      data-testid={`buscador-item-${it.value}`}
                    >
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badge.cls} shrink-0`}>{badge.txt}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{it.label}</div>
                        <div className="text-[10px] text-gray-500 truncate">{it.subLabel}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <span className="text-[10px] text-gray-500 shrink-0">Presiona una coincidencia para abrir el detalle de su OT</span>
        </div>
      </div>

      {contarSobrefacturadas() > 0 && (
        <div className="mb-2 flex items-center gap-2 text-[11px] bg-red-50 border border-red-300 text-red-700 rounded px-2 py-1" data-testid="alerta-sobrefacturadas">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span><b>{contarSobrefacturadas()}</b> OT(s) sobre-facturada(s) — facturado mayor al monto, marcadas en rojo en la columna SALDO</span>
        </div>
      )}

      {sinOTInfo?.cantidad > 0 && (
        <div className="mb-2 flex items-center gap-2 text-[11px] bg-amber-50 border border-amber-300 text-amber-800 rounded px-2 py-1" data-testid="alerta-facturas-sin-ot">
          <FileText className="w-3.5 h-3.5 shrink-0" />
          <span>
            <b>{sinOTInfo.cantidad}</b> facturas/NC sin OT asignada ({sinOTInfo.cantidad_anio_actual} del {sinOTInfo.anio_actual}) por <b>${fmt(sinOTInfo.total_neto)}</b> neto — no suman al Facturado de las OTs.
          </span>
          <button onClick={() => setShowSinOT(true)} className="underline font-semibold hover:text-amber-950" data-testid="btn-ver-facturas-sin-ot">
            Ver listado
          </button>
        </div>
      )}

      <div className="grid grid-cols-5 gap-2 mb-3 text-[10px]">
        <div className="bg-gray-100 border rounded p-2 text-center"><div className="text-gray-500">MONTO TOTAL</div><div className="font-bold text-sm">${fmt(data.totales.monto)}</div></div>
        <div className="bg-green-50 border border-green-200 rounded p-2 text-center"><div className="text-green-600">FACTURADO</div><div className="font-bold text-sm text-green-700">${fmt(data.totales.facturado)}</div></div>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center"><div className="text-blue-600">SALDO</div><div className="font-bold text-sm text-blue-700">${fmt(data.totales.saldo_retencion)}</div></div>
        <div className="bg-orange-50 border border-orange-200 rounded p-2 text-center"><div className="text-orange-600">x FACTURAR</div><div className="font-bold text-sm text-orange-700">${fmt(data.totales.por_facturar_ganado)}</div></div>
        <div className="bg-purple-100 border border-purple-300 rounded p-2 text-center"><div className="text-purple-600 font-medium">FACT. PROX MES</div><div className="font-bold text-lg text-purple-800">${fmt(data.totales.fact_prox_mes)}</div></div>
      </div>

      {showFacturas && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[85vh] overflow-hidden">
            <div className="flex justify-between items-center p-3 border-b bg-gray-100">
              <h3 className="font-bold text-sm flex items-center gap-2"><FileText className="w-4 h-4" />Facturas: {showFacturas.nombre}</h3>
              <Button variant="ghost" size="sm" onClick={closeFacturas} className="h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-3 overflow-auto max-h-[calc(85vh-60px)]">
              {loadingFacturas ? <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div> :
               facturas.length === 0 ? <p className="text-center py-8 text-muted-foreground">No hay facturas</p> :
              // Iter 313.j.16 (Renzo) — columnas Tipo Doc / OC / HES; totales redondeados
              // Iter 313.j.19 (Renzo) — columna Observaciones (NC anulatoria)
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border p-1">N° Documento</th>
                    <th className="border p-1">Fecha</th>
                    <th className="border p-1">Tipo</th>
                    <th className="border p-1">OC</th>
                    <th className="border p-1">HES</th>
                    <th className="border p-1">Observaciones</th>
                    <th className="border p-1 text-right">Neto</th>
                    <th className="border p-1 text-right">Total</th>
                    <th className="border p-1 w-8"></th>
                  </tr>
                </thead>
                <tbody>{facturas.map((f, idx) => {
                  const tipo = f.tipo_documento || (String(f.numero_factura||'').replace('.0','') === '99999' ? 'Proforma' : 'Factura');
                  const isProforma = tipo === 'Proforma';
                  const numDisp = f.numero_documento_display || String(f.numero_factura||'').replace('.0','') || '—';
                  const anulada = !!(f.observacion || '').trim();
                  return (
                    <tr key={idx} className={anulada ? 'bg-red-50' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                      <td className={`border p-1 font-mono ${anulada ? 'line-through text-gray-500' : ''}`}>
                        {numDisp}
                        {f.es_compartida && (
                          <div className="text-[9px] font-sans font-normal not-italic leading-tight mt-0.5 text-amber-700" data-testid={`factura-compartida-${numDisp}`}>
                            parte de factura compartida N° {f.folio_compartido || numDisp} (total ${fmt(Math.round(f.total_neto_factura || 0))})
                          </div>
                        )}
                      </td>
                      <td className={`border p-1 ${anulada ? 'text-gray-500' : ''}`}>{fmtFecha(f.fecha_emision || f.fecha)}</td>
                      <td className="border p-1 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          isProforma ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                          tipo === 'F. Exenta' ? 'bg-slate-100 text-slate-700 border border-slate-300' :
                          tipo === 'N. Crédito' ? 'bg-red-100 text-red-700 border border-red-300' :
                          'bg-emerald-100 text-emerald-700 border border-emerald-300'
                        }`}>{tipo}</span>
                      </td>
                      <td className={`border p-1 font-mono text-[10px] ${anulada ? 'text-gray-400' : 'text-blue-700'}`}>{f.orden_compra || '—'}</td>
                      <td className={`border p-1 font-mono text-[10px] ${anulada ? 'text-gray-400' : 'text-purple-700'}`}>{f.hes || '—'}</td>
                      <td className={`border p-1 text-[10px] ${anulada ? 'text-red-600 font-semibold' : 'text-gray-500 italic'}`}>{f.observacion || ''}</td>
                      <td className={`border p-1 text-right ${anulada ? 'text-gray-400' : ''}`}>${fmt(Math.round(f.neto || 0))}</td>
                      <td className={`border p-1 text-right ${anulada ? 'text-gray-400' : ''}`}>${fmt(Math.round(f.total || 0))}</td>
                      <td className="border p-1 text-center">
                        {isProforma && !anulada && f.id_venta && (
                          <button
                            onClick={() => openAnularProforma(f)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-100 rounded p-1"
                            title="Anular esta Proforma"
                            data-testid={`btn-anular-proforma-${f.id_venta}`}
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}</tbody>
                {/* Iter 313.j.23 (Renzo, feb-2026) — pie con sumas de Neto y Total */}
                <tfoot>
                  <tr className="bg-gray-100 font-semibold border-t-2 border-gray-400">
                    <td colSpan={6} className="border p-1 text-right text-[11px]">Totales:</td>
                    <td className="border p-1 text-right text-[11px]" data-testid="modal-facturas-total-neto">
                      ${fmt(Math.round(facturas.reduce((s, f) => s + (Number(f.neto) || 0), 0)))}
                    </td>
                    <td className="border p-1 text-right text-[11px]" data-testid="modal-facturas-total-total">
                      ${fmt(Math.round(facturas.reduce((s, f) => s + (Number(f.total) || 0), 0)))}
                    </td>
                    <td className="border p-1"></td>
                  </tr>
                </tfoot>
              </table>}
            </div>
          </div>
        </div>
      )}

      {/* Iter 313.j.22 (Renzo, feb-2026) — Modal de anulación manual de Proforma */}
      <Dialog open={!!anularModal} onOpenChange={(o) => !o && closeAnularProforma()}>
        <DialogContent className="max-w-md" data-testid="modal-anular-proforma">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="w-5 h-5" /> Anular Proforma
            </DialogTitle>
          </DialogHeader>
          {anularModal?.venta && (
            <div className="space-y-3 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs space-y-0.5">
                <div><b>N° Documento:</b> {anularModal.venta.numero_documento_display || anularModal.venta.numero_factura}</div>
                <div><b>Fecha:</b> {fmtFecha(anularModal.venta.fecha_emision || anularModal.venta.fecha)}</div>
                <div><b>OT:</b> {anularModal.venta.ot_numero || '—'}</div>
                <div><b>OC:</b> {anularModal.venta.orden_compra || '—'} · <b>HES:</b> {anularModal.venta.hes || '—'}</div>
                <div><b>Neto:</b> ${fmt(Math.round(anularModal.venta.neto || 0))} · <b>Total:</b> ${fmt(Math.round(anularModal.venta.total || 0))}</div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Motivo de la anulación <span className="text-red-600">*</span></label>
                <Textarea
                  value={anularMotivo}
                  onChange={(e) => setAnularMotivo(e.target.value)}
                  placeholder="Ej: emitida por error, duplicada, monto incorrecto…"
                  rows={2}
                  className="text-xs"
                  data-testid="input-motivo-anulacion"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Documento donde quedó facturado <span className="text-red-600">*</span></label>
                <Input
                  value={anularDocFacturado}
                  onChange={(e) => setAnularDocFacturado(e.target.value)}
                  placeholder="Ej: Factura N° 2444"
                  className="text-xs"
                  data-testid="input-doc-facturado"
                />
                <p className="text-[10px] text-gray-500 mt-1">Si no fue facturado, indícalo (ej: &quot;No facturado, error de emisión&quot;).</p>
              </div>
              <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
                ⚠ La anulación deja el Neto/IVA/Total en $0 y marca la Proforma con &quot;line-through&quot; en la vista. Los montos anteriores se preservan para trazabilidad. La acción se registra en el log de auditoría.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={closeAnularProforma} disabled={anularSaving} data-testid="btn-cancelar-anular">
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={confirmarAnularProforma}
              disabled={anularSaving || !anularMotivo.trim() || !anularDocFacturado.trim()}
              data-testid="btn-confirmar-anular"
            >
              {anularSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Ban className="w-3.5 h-3.5 mr-1" />}
              Confirmar Anulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="border rounded-lg overflow-auto max-h-[calc(100vh-280px)]" data-testid="tabla-circulante">
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="text-white">
              <th className="sticky top-0 z-20 bg-gray-800 border-b border-gray-600 p-1 text-left w-10">OT</th>
              <th className="sticky top-0 z-20 bg-gray-800 border-b border-gray-600 p-1 text-left">DESCRIPCIÓN</th>
              <th className="sticky top-0 z-20 bg-gray-800 border-b border-gray-600 p-1 text-right w-20">MONTO</th>
              <th className="sticky top-0 z-20 bg-gray-800 border-b border-gray-600 p-1 text-right w-20">FACT.</th>
              <th className="sticky top-0 z-20 bg-gray-800 border-b border-gray-600 p-1 text-right w-20">SALDO</th>
              <th className="sticky top-0 z-20 bg-yellow-700 border-b border-gray-600 p-1 text-center w-12">AV.FIN</th>
              <th className="sticky top-0 z-20 bg-gray-800 border-b border-gray-600 p-1 text-right w-20">x FACT</th>
              <th className="sticky top-0 z-20 bg-yellow-700 border-b border-gray-600 p-1 text-center w-12">AV.FIS</th>
              <th className="sticky top-0 z-20 bg-purple-700 border-b border-gray-600 p-1 text-center w-20">PROX.MES</th>
              <th className="sticky top-0 z-20 bg-gray-800 border-b border-gray-600 p-1 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {data.proyectos.map((proy) => (
              <React.Fragment key={proy.proyecto_codigo}>
                {/* PROYECTO */}
                <tr className="bg-slate-700 text-white font-bold cursor-pointer hover:bg-slate-600" onClick={() => toggleProyecto(proy.proyecto_codigo)}>
                  <td colSpan={2} className="p-2">
                    <div className="flex items-center gap-2">
                      {expandedProyectos[proy.proyecto_codigo] ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />}
                      <span>📁 {proy.proyecto_codigo} - {proy.cliente}</span>
                    </div>
                  </td>
                  <td className="p-1 text-right font-mono">${fmt(proy.totales.monto)}</td>
                  <td className="p-1 text-right font-mono text-green-300">${fmt(proy.totales.facturado)}</td>
                  <td className="p-1 text-right font-mono text-blue-300">${fmt(proy.totales.saldo_retencion)}</td>
                  <td className="p-1"></td>
                  <td className="p-1 text-right font-mono text-orange-300">${fmt(proy.totales.por_facturar_ganado)}</td>
                  <td className="p-1"></td>
                  <td className="p-1 text-right font-mono text-purple-300">${fmt(proy.totales.fact_prox_mes)}</td>
                  <td className="p-1"></td>
                </tr>

                {expandedProyectos[proy.proyecto_codigo] && (
                  <>
                    {/* SUBPROYECTOS (grupos con más de 1 OT) */}
                    {proy.subproyectos?.map((sp) => {
                      const spKey = `${proy.proyecto_codigo}-${sp.orden_compra}`;
                      const isExpanded = expandedSubproyectos[spKey];
                      return (
                        <React.Fragment key={spKey}>
                          <tr className="bg-blue-100 font-semibold cursor-pointer hover:bg-blue-200" onClick={() => toggleSubproyecto(proy.proyecto_codigo, sp.orden_compra)}>
                            <td colSpan={2} className="p-1 pl-6">
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                <span>📂 {sp.nombre?.substring(0, 45)}...</span>
                                <span className="text-blue-600 text-[9px]">({sp.total_ots} OTs)</span>
                              </div>
                            </td>
                            <td className="p-1 text-right font-mono">${fmt(sp.total_combinado?.monto)}</td>
                            <td className="p-1 text-right font-mono text-green-700">${fmt(sp.total_combinado?.facturado)}</td>
                            <td className="p-1 text-right font-mono text-blue-700">${fmt(sp.total_combinado?.saldo_retencion)}</td>
                            <td className="p-1"></td>
                            <td className="p-1 text-right font-mono text-orange-600">${fmt(sp.total_combinado?.por_facturar_ganado)}</td>
                            <td className="p-1"></td>
                            <td className="p-1 text-right font-mono text-purple-700">${fmt(sp.total_combinado?.fact_prox_mes)}</td>
                            <td className="p-1"></td>
                          </tr>

                          {isExpanded && sp.ots?.map((ot, idx) => renderOTRow(ot, 40, idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'))}
                          
                          {isExpanded && sp.aumentos_obra?.length > 0 && (
                            <>
                              <tr className="bg-amber-100"><td colSpan={10} className="p-1 pl-10 font-semibold text-amber-800">🔸 AUMENTOS DE OBRA ({sp.aumentos_obra.length})</td></tr>
                              {sp.aumentos_obra.map((ao, idx) => (
                                <tr key={ao.numero_ot} className={idx % 2 === 0 ? 'bg-amber-50' : 'bg-amber-100/50'}>
                                  <td className="border-b border-amber-200 p-1 text-center font-mono pl-12 whitespace-nowrap">
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${ao.estado === 'EN EJECUCION' ? 'bg-green-500' : 'bg-gray-400'}`} title={ao.estado || 'Sin estado'}></span>
                                    <button onClick={() => irADatosOT(ao)} className="text-blue-700 font-semibold hover:underline cursor-pointer" title="Abrir Datos de la OT" data-testid={`link-ot-${ao.numero_ot}`}>{ao.numero_ot}</button>
                                  </td>
                                  <td className="border-b border-amber-200 p-1 pl-12 truncate italic text-amber-800">(AO) {ao.descripcion}</td>
                                  <td className="border-b border-amber-200 p-1 text-right font-mono">${fmt(ao.monto)}</td>
                                  <td className="border-b border-amber-200 p-1 text-right font-mono text-green-700">${fmt(ao.facturado)}</td>
                                  {renderSaldoCell(ao, 'border-amber-200')}
                                  <td className="border-b border-amber-200 p-1 text-center bg-yellow-50">{renderEditableCell(ao, 'av_financiero', ao.av_financiero)}</td>
                                  <td className="border-b border-amber-200 p-1 text-right font-mono text-orange-600">${fmt(ao.por_facturar_ganado)}</td>
                                  <td className="border-b border-amber-200 p-1 text-center bg-yellow-50">{renderEditableCell(ao, 'av_fisico', ao.av_fisico)}</td>
                                  <td className="border-b border-amber-200 p-1 text-center bg-purple-50">{renderEditableCell(ao, 'fact_prox_mes', ao.fact_prox_mes, 'text-purple-700')}</td>
                                  <td className="border-b border-amber-200 p-1">{renderAcciones(ao)}</td>
                                </tr>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {/* OTs INDIVIDUALES (con O/C pero solo 1 OT) */}
                    {proy.ots_individuales?.map((ot, idx) => renderOTRow(ot, 20, idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'))}

                    {/* OTs SIN O/C (al final, en gris) */}
                    {proy.ots_sin_oc?.length > 0 && (
                      <>
                        <tr className="bg-gray-200"><td colSpan={10} className="p-1 pl-6 text-gray-600 text-[9px]">— Sin Orden de Compra ({proy.ots_sin_oc.length}) —</td></tr>
                        {proy.ots_sin_oc.map((ot, idx) => renderOTRow(ot, 20, idx % 2 === 0 ? 'bg-gray-100' : 'bg-gray-50'))}
                      </>
                    )}
                  </>
                )}
              </React.Fragment>
            ))}

            {/* TOTALES */}
            <tr className="bg-gray-800 text-white font-bold">
              <td colSpan={2} className="p-2">TOTALES ({data.totales.cantidad_proyectos} Proyectos, {data.totales.cantidad_ots} OTs)</td>
              <td className="p-1 text-right font-mono">${fmt(data.totales.monto)}</td>
              <td className="p-1 text-right font-mono">${fmt(data.totales.facturado)}</td>
              <td className="p-1 text-right font-mono">${fmt(data.totales.saldo_retencion)}</td>
              <td className="p-1"></td>
              <td className="p-1 text-right font-mono">${fmt(data.totales.por_facturar_ganado)}</td>
              <td className="p-1"></td>
              <td className="p-1 text-right font-mono text-purple-300">${fmt(data.totales.fact_prox_mes)}</td>
              <td className="p-1"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <OTDatosModal otId={otModal?.id} open={!!otModal} onClose={(saved) => { setOtModal(null); if (saved) loadData(); }} />
      <EnvioGerenciaDialog open={envioOpen} onClose={() => setEnvioOpen(false)} />
      <FacturasSinOTModal open={showSinOT} onClose={() => setShowSinOT(false)} onAssigned={() => { loadSinOT(); loadData(); }} />
    </div>
  );
};
